import { Capacitor } from '@capacitor/core';
import { paths } from './paths';
import { removeValue, writeValue } from './rtdb';
import { requireUid } from './auth';

/**
 * Ou joindre un membre quand il n'est pas devant l'application.
 *
 * Un jeton d'appareil est delivre par le systeme, pas par nous : c'est
 * l'iPhone qui le donne apres avoir accorde la permission, et il change
 * tout seul — reinstallation, restauration, expiration. On le range donc
 * en cle plutot qu'en valeur, un membre pouvant avoir plusieurs
 * appareils, et on le reecrit a chaque lancement plutot que de supposer
 * qu'il tient.
 *
 * Personne ne peut lire les jetons d'un autre : la regle les reserve a
 * leur proprietaire. Les fonctions qui envoient les notifications passent
 * par le SDK Admin, au-dessus des regles.
 *
 * Rien n'appelle encore ces fonctions : la permission d'envoyer des
 * notifications distantes n'est pas accordee a la signature actuelle de
 * l'application. Voir `functions/src/notifications.ts` pour la moitie qui
 * envoie, et le meme constat.
 */

/**
 * Retient un appareil pour ce membre.
 *
 * Appelable a chaque lancement sans precaution : reecrire un jeton connu
 * ne fait que rafraichir sa date, et c'est cette date qui permettra un
 * jour d'oublier les appareils qu'on ne voit plus.
 *
 * La plateforme accompagne le jeton : le serveur ne sonne pas un iPhone
 * comme un Android. L'un recoit un envoi VoIP pour CallKit, l'autre un
 * message de donnees qui reveille sa sonnerie plein ecran.
 */
export async function saveDeviceToken(token: string): Promise<void> {
  const uid = requireUid();
  await writeValue(paths.deviceToken(uid, token), {
    platform: Capacitor.getPlatform(),
    updatedAt: Date.now(),
  });
}

/**
 * Oublie un appareil.
 *
 * A la deconnexion : le telephone reste celui de quelqu'un, mais plus
 * celui de ce compte-la. Sans cet oubli, le membre suivant recevrait les
 * messages du precedent.
 */
export async function removeDeviceToken(token: string): Promise<void> {
  const uid = requireUid();
  await removeValue(paths.deviceToken(uid, token));
}

